<template lang="pug">
yz-dialog(v-model="showDialog" fullscreen persistent fluid scrollable max-width="1000")
  yz-card
    yz-dialog-fullscreen-header(title="View branch details", no-actions @back="onClose")
    yz-dialog-fullscreen-content
      div(style="max-width: 850px; margin: 0 auto;")
        //- Branch Details Card
        yz-card.mb-4(outlined)
          .pa-4
            .yz-medium-body-sm.neutral700--text.mb-3 Branch details
            v-row
              v-col(cols="12" md="4")
                .yz-regular-body-xs.neutral500--text.mb-1 Name
                .yz-medium-body-sm.neutral700--text {{ branchData.name || '-' }}
              v-col(cols="12" md="4")
                .yz-regular-body-xs.neutral500--text.mb-1 Address
                .yz-medium-body-sm.neutral700--text {{ formattedAddress || '-' }}
              v-col(cols="12" md="4")
                .yz-regular-body-xs.neutral500--text.mb-1 Contact details
                .d-flex.flex-column
                  .yz-medium-body-sm.neutral700--text.mb-1 {{ branchData.email || '-' }}
                  div
                    yz-btn(v-if="branchData.phone" underline :label="'+' + branchData.phone" append-icon="mdi-whatsapp" icon-color="neutral-500" @click="onOpenWhatsApp")

        //- Storage Locations Card
        yz-card(outlined)
          .pa-4
            .d-flex.align-center.justify-space-between.mb-3
              .yz-medium-body-sm.neutral700--text Storage locations
              yz-btn(v-if="canManageStorage" prepend-icon="$yzi-plus" label="Add storage" @click="onAddStorage")

            //- Empty State
            yz-empty-state-card(v-if="storageLocations.length === 0" icon="$yzi-colors" title="No items selected" description="Any selected items from your list will show up here" no-action)

            //- Filled State - Data Table
            yz-data-table(v-else :headers="computedStorageHeaders" :items="storageLocations" header-class="yz-medium-body-xs" body-class="yz-regular-body-sm")
              template(v-slot:item.name="{ item }")
                span.font-medium {{ item.name }}
              template(v-slot:item.createdDate="{ item }")
                span {{ item.createdDate }}
              template(v-slot:item.inventories="{ item }")
                span {{ item.inventories }}
              template(v-slot:item.actions="{ item }")
                .d-flex.align-center(v-if="canManageStorage")
                  yz-btn.mr-2(icon="$yzi-edit-01" outlined, @click="onEditStorage(item)")
                  yz-btn(icon="$yzi-trash-03" outlined, icon-color="yz-danger-500" @click="onDeleteStorage(item)")

    //- Dialogs
    add-storage-location-dialog(v-if="showStorageDialog && canManageStorage" :open.sync="showStorageDialog" :selected-branch="selectedBranch" :storage-location="editingStorage" @saved="onStorageSaved" @deleted="onStorageDeleted")
    yz-delete-confirmation-dialog(v-if="canManageStorage" v-model="showDeleteConfirmDialog" item="storage location" description="This action will permanently delete the storage location and remove all items from it. This cannot be undone." :loading="isDeleting" @confirmed="confirmDeleteStorage" @canceled="cancelDeleteStorage")
      yz-card.mt-4.pa-4(v-if="deletingStorage" outlined)
        .yz-medium-body-sm.text-neutral-700 {{ deletingStorage.name }}
        .yz-regular-body-xs.text-neutral-500.mt-2(v-if="deletingStorage.inventories > 0") {{ deletingStorage.inventories }} item(s) will be removed from this location
</template>

<script>
import { mapActions } from 'vuex'
import AddStorageLocationDialog from './AddStorageLocationDialog.vue'

export default {
  components: {
    AddStorageLocationDialog
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    selectedBranch: {
      type: Object,
      default: null
    },
    isHqMode: {
      type: Boolean,
      default: true
    },
    currentCompanySlug: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      storageHeaders: [
        { text: 'Storage location', value: 'name', sortable: false },
        { text: 'Created date', value: 'createdDate', sortable: false },
        { text: 'Inventories', value: 'inventories', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end', width: '100px' }
      ],
      showStorageDialog: false,
      editingStorage: null,
      showDeleteConfirmDialog: false,
      deletingStorage: null,
      isDeleting: false
    }
  },
  computed: {
    showDialog: {
      get() {
        return this.open
      },
      set(value) {
        this.$emit('update:open', value)
      }
    },
    branchData() {
      if (!this.selectedBranch) {
        return {
          id: null,
          name: '',
          address: null,
          email: '',
          phone: '',
          storageLocations: []
        }
      }
      return this.selectedBranch
    },
    formattedAddress() {
      const address = this.branchData.address
      if (!address) return null
      if (typeof address === 'string') return address
      const parts = [address.address_1, address.address_2, address.postcode, address.city, address.state].filter(Boolean)
      return parts.join(', ')
    },
    storageLocations() {
      return this.branchData.storageLocations || []
    },
    isOwnBranch() {
      return this.currentCompanySlug && this.selectedBranch?.slug === this.currentCompanySlug
    },
    canEditBranch() {
      // Only own branch can edit their branch details
      return this.isOwnBranch
    },
    canManageStorage() {
      // HQ or own branch can manage storage
      return this.isHqMode || this.isOwnBranch
    },
    computedStorageHeaders() {
      // Remove actions column if user cannot manage storage
      if (!this.canManageStorage) {
        return this.storageHeaders.filter(h => h.value !== 'actions')
      }
      return this.storageHeaders
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    onClose() {
      this.showDialog = false
    },
    onOpenWhatsApp() {
      if (!this.branchData.phone) return
      const cleanPhone = this.branchData.phone.replace(/[^0-9]/g, '')
      window.open(`https://api.whatsapp.com/send?phone=${cleanPhone}&app_absent=1`)
    },
    onAddStorage() {
      if (!this.canManageStorage) return
      this.editingStorage = null
      this.showStorageDialog = true
    },
    onEditStorage(item) {
      if (!this.canManageStorage) return
      this.editingStorage = item
      this.showStorageDialog = true
    },
    onDeleteStorage(item) {
      if (!this.canManageStorage) return
      this.deletingStorage = item
      this.showDeleteConfirmDialog = true
    },
    async confirmDeleteStorage() {
      if (!this.deletingStorage || !this.selectedBranch?.slug || !this.canManageStorage) return

      this.isDeleting = true
      try {
        await this.$axios.delete(`/clinics/${this.selectedBranch.slug}/inventories/locations/${this.deletingStorage.id}`)
        this.showMessage(['success', 'Storage location deleted'])
        this.$emit('refresh')
        this.showDeleteConfirmDialog = false
        this.deletingStorage = null
      } catch (error) {
        console.error('Error deleting storage location:', error)
        const errorMessage = error.response?.data?.detail || 'Failed to delete storage location'
        this.showMessage(['error', errorMessage])
      } finally {
        this.isDeleting = false
      }
    },
    cancelDeleteStorage() {
      this.showDeleteConfirmDialog = false
      this.deletingStorage = null
    },
    onStorageSaved() {
      this.$emit('refresh')
      this.showStorageDialog = false
      this.editingStorage = null
    },
    onStorageDeleted() {
      this.$emit('refresh')
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
